/**
 * UI-8f Task 9 — IncidentDetailPage.
 *
 * Per-incident detail view. Composition:
 *
 *   - <RequirePermission permission="platform:incidents_admin"> gate.
 *   - Back link to /platform/incidents (breadcrumb-style; matches the
 *     TenantDetailPage pattern from UI-8a).
 *   - PageHeader with incident.title + severity + status badges.
 *   - <Card title="Description"> with incident.description.
 *   - <IncidentTimeline> for notes + the append-note UI.
 *   - <Card title="Response Actions"> when status is RESOLVED or CLOSED.
 *   - <Card title="Lessons Learned"> when status === 'CLOSED'.
 *   - Action button row gated by current status:
 *       OPEN          → "Acknowledge / Investigate" (→ INVESTIGATING)
 *                     + "False alarm (Close)" (→ CLOSED, no required text)
 *       INVESTIGATING → "Resolve…" (opens ResolveDialog)
 *       RESOLVED      → "Close…" (opens CloseDialog)
 *       CLOSED        → no actions (terminal)
 *   - ResolveDialog + CloseDialog mounted at the page level.
 *
 * The detail page uses GET /platform/incidents/:id (NOT the list) so a
 * direct deep-link works without pre-loading the list. `getOne` is wrapped
 * in a local `refetch` callback so transitions / note appends can
 * re-pull the freshest row.
 *
 * Permission: PLATFORM_INCIDENTS_ADMIN. Sole client-side gate.
 */
import {
  useCallback,
  useEffect,
  useState,
  type JSX,
} from 'react'
import { Link, useParams } from 'react-router'
import { Button } from 'primereact/button'
import { Card } from 'primereact/card'
import { ProgressSpinner } from 'primereact/progressspinner'
import { Tag } from 'primereact/tag'

import PageHeader from '../../components/admin/PageHeader.js'
import RequirePermission from '../../components/auth/RequirePermission.js'
import { CloseDialog } from '../../components/platform/CloseDialog.js'
import { IncidentTimeline } from '../../components/platform/IncidentTimeline.js'
import { ResolveDialog } from '../../components/platform/ResolveDialog.js'
import { useIncidents, type Incident } from '../../hooks/useIncidents.js'

// ---------------------------------------------------------------------------
// Badge helpers (mirrors IncidentsPage)
// ---------------------------------------------------------------------------

function severityBadge(s: Incident['severity']): JSX.Element {
  const sev =
    s === 'CRITICAL'
      ? 'danger'
      : s === 'HIGH'
        ? 'warning'
        : s === 'MEDIUM'
          ? 'info'
          : 'secondary'
  return <Tag value={s} severity={sev} />
}

function statusBadge(s: Incident['status']): JSX.Element {
  const sev =
    s === 'OPEN'
      ? 'warning'
      : s === 'INVESTIGATING'
        ? 'info'
        : s === 'RESOLVED'
          ? 'success'
          : 'secondary'
  return <Tag value={s} severity={sev} />
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function IncidentDetailPage(): JSX.Element {
  return (
    <RequirePermission permission="platform:incidents_admin">
      <IncidentDetailInner />
    </RequirePermission>
  )
}

function IncidentDetailInner(): JSX.Element {
  const params = useParams<{ id: string }>()
  const id = params.id
  const { getOne, transition, appendNote } = useIncidents()

  const [incident, setIncident] = useState<Incident | null>(null)
  const [loading, setLoading] = useState(true)
  const [resolveOpen, setResolveOpen] = useState(false)
  const [closeOpen, setCloseOpen] = useState(false)

  const refetch = useCallback(async (): Promise<void> => {
    if (!id) return
    setLoading(true)
    try {
      const fresh = await getOne(id)
      setIncident(fresh)
    } catch {
      setIncident(null)
    } finally {
      setLoading(false)
    }
  }, [id, getOne])

  useEffect(() => {
    void refetch()
  }, [refetch])

  // --- Loading / not-found branches ---------------------------------------

  if (loading && incident === null) {
    return (
      <div className="px-8 py-6 space-y-4">
        <div className="flex justify-center py-12">
          <ProgressSpinner />
        </div>
      </div>
    )
  }

  if (incident === null) {
    return (
      <div className="px-8 py-6 space-y-4">
        <PageHeader
          title="Incident not found"
          breadcrumbs={[{ label: 'Incidents', to: '/platform/incidents' }]}
        />
        <div className="rounded border border-gray-200 p-6 text-center">
          <p className="text-gray-700">Incident not found.</p>
          <Link
            to="/platform/incidents"
            className="mt-2 inline-block text-blue-600 hover:underline"
          >
            Back to Incidents
          </Link>
        </div>
      </div>
    )
  }

  // --- Transition handlers ------------------------------------------------

  async function handleAcknowledge(): Promise<void> {
    await transition(incident!.id, { to: 'INVESTIGATING' })
    await refetch()
  }

  async function handleFastClose(): Promise<void> {
    // OPEN → CLOSED (false alarm) — no required text.
    await transition(incident!.id, { to: 'CLOSED' })
    await refetch()
  }

  async function handleResolveSubmit(responseActions: string): Promise<void> {
    await transition(incident!.id, { to: 'RESOLVED', responseActions })
    setResolveOpen(false)
    await refetch()
  }

  async function handleCloseSubmit(lessonsLearned: string): Promise<void> {
    await transition(incident!.id, { to: 'CLOSED', lessonsLearned })
    setCloseOpen(false)
    await refetch()
  }

  async function handleAddNote(text: string): Promise<void> {
    await appendNote(incident!.id, text)
    await refetch()
  }

  // --- Render --------------------------------------------------------------

  return (
    <div className="px-8 py-6 space-y-4">
      <PageHeader
        title={incident.title}
        description={`Detected ${incident.detectedAt}`}
        breadcrumbs={[
          { label: 'Incidents', to: '/platform/incidents' },
          { label: incident.title },
        ]}
        actions={
          <span className="inline-flex items-center gap-2">
            {severityBadge(incident.severity)}
            {statusBadge(incident.status)}
          </span>
        }
      />

      <Card title="Description">
        <p className="whitespace-pre-wrap text-sm text-gray-800">
          {incident.description}
        </p>
      </Card>

      <IncidentTimeline incident={incident} onAddNote={handleAddNote} />

      {(incident.status === 'RESOLVED' || incident.status === 'CLOSED') && (
        <Card title="Response Actions">
          <p className="whitespace-pre-wrap text-sm text-gray-800">
            {incident.responseActions ?? '—'}
          </p>
        </Card>
      )}

      {incident.status === 'CLOSED' && (
        <Card title="Lessons Learned">
          <p className="whitespace-pre-wrap text-sm text-gray-800">
            {incident.lessonsLearned ?? '—'}
          </p>
        </Card>
      )}

      <div className="flex flex-wrap gap-2">
        {incident.status === 'OPEN' && (
          <>
            <Button
              label="Acknowledge / Investigate"
              onClick={() => {
                void handleAcknowledge()
              }}
            />
            <Button
              label="False alarm (Close)"
              severity="secondary"
              onClick={() => {
                void handleFastClose()
              }}
            />
          </>
        )}
        {incident.status === 'INVESTIGATING' && (
          <Button
            label="Resolve…"
            onClick={() => {
              setResolveOpen(true)
            }}
          />
        )}
        {incident.status === 'RESOLVED' && (
          <Button
            label="Close…"
            onClick={() => {
              setCloseOpen(true)
            }}
          />
        )}
      </div>

      <ResolveDialog
        open={resolveOpen}
        onClose={() => {
          setResolveOpen(false)
        }}
        onSubmit={handleResolveSubmit}
      />
      <CloseDialog
        open={closeOpen}
        onClose={() => {
          setCloseOpen(false)
        }}
        onSubmit={handleCloseSubmit}
      />
    </div>
  )
}


